/**
 * Shared helpers for attaching predictable `data-testid`s to components.
 *
 * Goals:
 * - One root id per composite; child slots derive as `${root}-<slot>`.
 * - Components accept the HTML-native `data-testid` prop only.
 * - Never emit an empty/undefined attribute (Playwright should see the id or nothing).
 */

export type TestIdProps = {
  "data-testid"?: string;
};

/** Reads the root id from the HTML-native `data-testid` prop. */
export function resolveTestId(props: TestIdProps): string | undefined {
  return props["data-testid"];
}

export type ScopedTestIds = {
  root: string | undefined;
  /** Full child id string, or undefined when there is no root. */
  child: (suffix: string) => string | undefined;
  /**
   * Spreadable props for JSX. Returns `{}` (attribute omitted) when there is no id,
   * so we never render `data-testid={undefined}`.
   */
  props: (suffix?: string) => { "data-testid"?: string };
};

/** Derives child ids from a single root id. */
export function scopedTestIds(root?: string): ScopedTestIds {
  const child = (suffix: string) => (root ? `${root}-${suffix}` : undefined);
  const props = (suffix?: string) => {
    const id = suffix ? child(suffix) : root;
    return id ? { "data-testid": id } : {};
  };
  return { root, child, props };
}
